import React from 'react';
import { Box, TextField, IconButton, Button, Autocomplete } from "@mui/material";
import { Trash as TrashIcon, PlusCircle as PlusCircleIcon } from "lucide-react";

const posOptions = [
  { label: 'Noun (N)', value: 'N' },
  { label: 'Proper Noun (PN)', value: 'PN' },
  { label: 'Common Noun (CN)', value: 'CN' },
  { label: 'Pronoun (PR)', value: 'PR' },
  { label: 'Personal Pronoun (PP)', value: 'PP' },
  { label: 'Possessive Pronoun (PP$)', value: 'PP$' },
  { label: 'Demonstrative Pronoun (DP)', value: 'DP' },
  { label: 'Reflexive Pronoun (RP)', value: 'RP' },
  { label: 'Interrogative Pronoun (IP)', value: 'IP' },
  { label: 'Relative Pronoun (RP)', value: 'RP' },
  { label: 'Indefinite Pronoun (IPR)', value: 'IPR' },
  { label: 'Verb (V)', value: 'V' },
  { label: 'Main Verb (MV)', value: 'MV' },
  { label: 'Auxiliary Verb (AUX)', value: 'AUX' },
  { label: 'Modal Verb (MD)', value: 'MD' },
  { label: 'Copula Verb (COP)', value: 'COP' },
  { label: 'Transitive Verb (TV)', value: 'TV' },
  { label: 'Intransitive Verb (IV)', value: 'IV' },
  { label: 'Participle (VBN, VBG)', value: 'VBN/VBG' },
  { label: 'Infinitive (VB)', value: 'VB' },
  { label: 'Gerund (VBG)', value: 'VBG' },
  { label: 'Finite Verb (VF)', value: 'VF' },
  { label: 'Non-finite Verb (VNF)', value: 'VNF' },
  { label: 'Adjective (ADJ)', value: 'ADJ' },
  { label: 'Superlative Adjective (JJS)', value: 'JJS' },
  { label: 'Comparative Adjective (JJR)', value: 'JJR' },
  { label: 'Absolute Adjective (JJA)', value: 'JJA' },
  { label: 'Adverb (ADV)', value: 'ADV' },
  { label: 'Comparative Adverb (RBR)', value: 'RBR' },
  { label: 'Superlative Adverb (RBS)', value: 'RBS' },
  { label: 'Determiner (DET)', value: 'DET' },
  { label: 'Definite Article (DA)', value: 'DA' },
  { label: 'Indefinite Article (IA)', value: 'IA' },
  { label: 'Demonstrative Determiner (DD)', value: 'DD' },
  { label: 'Possessive Determiner (PD)', value: 'PD' },
  { label: 'Interrogative Determiner (ID)', value: 'ID' },
  { label: 'Quantifier (Q)', value: 'Q' },
  { label: 'Preposition (P)', value: 'P' },
  { label: 'Postposition (POST)', value: 'POST' },
  { label: 'Adposition (ADP)', value: 'ADP' },
  { label: 'Coordinating Conjunction (CC)', value: 'CC' },
  { label: 'Subordinating Conjunction (SC)', value: 'SC' },
  { label: 'Correlative Conjunction (CORC)', value: 'CORC' },
  { label: 'Particle (PART)', value: 'PART' },
  { label: 'Negative Particle (NEG)', value: 'NEG' },
  { label: 'Infinitive Marker (IM)', value: 'IM' },
  { label: 'Focus Particle (FP)', value: 'FP' },
  { label: 'Interjection (INTJ)', value: 'INTJ' },
  { label: 'Cardinal Number (CD)', value: 'CD' },
  { label: 'Ordinal Number (ON)', value: 'ON' },
  { label: 'Numeral (NUM)', value: 'NUM' },
  { label: 'Fraction (FRAC)', value: 'FRAC' },
  { label: 'Multiplicative Numeral (MNUM)', value: 'MNUM' }
];

const VocabularyEditor = ({ vocabulary, onChange, errors }) => {
  const handleFieldChange = (index, field, value) => {
    const newVocab = [...vocabulary];
    newVocab[index] = { ...newVocab[index], [field]: value };
    onChange(newVocab);
  };

  const handleRemove = (index) => {
    const newVocab = vocabulary.filter((_, i) => i !== index);
    onChange(newVocab);
  };

  const handleAdd = () => {
    onChange([...vocabulary, { word: '', partOfSpeech: '', meanings: [] }]);
  };

  return (
    <Box sx={{ mt: 1 }}>
      {vocabulary.map((item, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
          <TextField
            label="Word"
            value={item.word}
            onChange={(e) => handleFieldChange(index, 'word', e.target.value)}
            size="small"
            error={Boolean(errors && errors[`vocab_${index}`] && !item.word.trim())}
            helperText={errors && errors[`vocab_${index}`] && !item.word.trim() ? errors[`vocab_${index}`] : ''}
          />
          <Autocomplete
            options={posOptions}
            getOptionLabel={(option) => option.label}
            filterOptions={(options, state) =>
              options.filter(option =>
                option.label.toLowerCase().includes(state.inputValue.toLowerCase())
              )
            }
            value={posOptions.find(opt => opt.value === item.partOfSpeech) || null}
            onChange={(e, newValue) => handleFieldChange(index, 'partOfSpeech', newValue ? newValue.value : '')}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="POS" 
                size="small"
                error={Boolean(errors && errors[`vocab_${index}`] && !item.partOfSpeech)}
                helperText={errors && errors[`vocab_${index}`] && !item.partOfSpeech ? errors[`vocab_${index}`] : ''}
              />
            )}
            sx={{ width: 250 }}
          />
          <TextField
            label="Meanings (comma separated)"
            value={item.meanings.join(', ')}
            onChange={(e) =>
              handleFieldChange(
                index,
                'meanings',
                e.target.value.split(',').map(s => s.trim())
              )
            }
            size="small"
            error={Boolean(errors && errors[`vocab_${index}`] && (!item.meanings || item.meanings.length === 0))}
            helperText={errors && errors[`vocab_${index}`] && (!item.meanings || item.meanings.length === 0) ? errors[`vocab_${index}`] : ''}
          />
          <IconButton color="error" onClick={() => handleRemove(index)}>
            <TrashIcon size={16} />
          </IconButton>
        </Box>
      ))}
      <Button variant="outlined" startIcon={<PlusCircleIcon size={16} />} onClick={handleAdd}>
        Add Vocabulary
      </Button>
    </Box>
  );
};

export default VocabularyEditor;
